import type { OrderDetailsExtended } from 'app/src/model'
import { familyMembersSchema } from 'app/src/msw/membership/schemas/familyMembers'
import type {
  Beneficiaries,
  MerchantLoginInfo,
  OrderDetailsPurchase,
  OrderDetailsPurchaseDiscount,
  OrderDetailsPurchaseEmpty,
  OrderDetailsPurchaseGiftCard,
  OrderDetailsPurchaseGoodAndService,
  OrderDetailsPurchaseMultiPurchase,
  OrderDetailsPurchasePass,
  OrderDetailsPurchaseVoucher,
  OrderDetailsVoucherUsage,
  OrderPriceDetails,
  UserInfo,
  VariantAndQuantity,
} from 'app/src/services/welfareportal/shopping-api'

// eslint-disable-next-line import/no-named-as-default
import z from 'zod'

const productSchema = z.object({
  productId: z.string(),
  externalId: z.string(),
  typology: z.enum(['moneyTransfer', 'refund', 'giftCard', 'goodAndService', 'pass', 'voucher', 'discount']),
  requiredAccessLevel: z.enum(['public', 'welfare']),
  merchantType: z.enum(['epipoli', 'amilon', 'pws', 'tantosvago', 'poste', 'entheos', 'osa', 'postewarehouse', 'lastminute']),
  serviceCategory: z.enum(['welfareArt100', 'welfareArt51', 'none']),
  productName: z.string(),
  signature: z.object({
    signature: z.string(),
    validUntil: z.number(),
  }),
  privacyList: z
    .array(
      z.object({
        privacyId: z.string(),
        mandatory: z.boolean(),
      }),
    )
    .optional(),
})

const OrderPriceDetailsSchema = z.object({
  grossPrice: z.number(),
  netPrice: z.number(),
  taxPercentage: z.number(),
  taxAmount: z.number(),
  taxClass: z.string().optional(),
  discountType: z.enum(['percentage', 'fixed']).optional(),
  discountValue: z.number().optional(),
  billingData: z.string().optional(),
}) satisfies z.ZodType<OrderPriceDetails>

const variantSchema = z.object({
  variantId: z.string(),
  externalId: z.string(),
  variantName: z.string(),
  priceDetails: z
    .object({
      billingData: z.string().optional(),
      discountType: z.enum(['percentage', 'fixed']).optional(),
      discountValue: z.number().optional(),
      facePrice: z.number(),
      finalGrossPrice: z.number(),
      taxClass: z.string().optional(),
      taxPercentage: z.number(),
    })
    .optional(),
  active: z.boolean(),
  signature: z.object({
    signature: z.string(),
    validUntil: z.number(),
  }),
})

const userInfoSchema: z.ZodType<UserInfo> = z.object({
  userId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  fiscalCode: z.string(),
  membershipId: z.string(),
})

const beneficiariesSchema: z.ZodType<Beneficiaries> = z.object({
  user: z.boolean(),
  familyMembers: z.array(familyMembersSchema),
})

const orderDetailsPurchaseEmptySchema = z.object({
  typology: z.literal('empty'),
}) satisfies z.ZodType<OrderDetailsPurchaseEmpty>

// Schema per OrderDetailsPurchaseGiftCard
const orderDetailsPurchaseGiftCardSchema = z.object({
  typology: z.literal('giftCard'),
  cardCode: z.string().optional(),
  cardPin: z.string().optional(),
  cardCodeImageUrl: z.string().optional(),
  cardDetailsUrl: z.string().optional(),
  cardActivationUrl: z.string().optional(),
  purchaseDate: z.string().datetime(),
  expirationDate: z.string().datetime().optional(),
}) satisfies z.ZodType<OrderDetailsPurchaseGiftCard>

// Schema per OrderDetailsPurchasePass
const orderDetailsPurchasePassSchema = z.object({
  typology: z.literal('pass'),
  qrCodeImageUrl: z.string(),
  purchaseDate: z.string().datetime(),
  expirationDate: z.string().datetime(),
}) satisfies z.ZodType<OrderDetailsPurchasePass>

// Schema per OrderDetailsPurchaseVoucher
const orderDetailsPurchaseVoucherSchema = z.object({
  typology: z.literal('voucher'),
  voucherImageUrl: z.string(),
  voucherCode: z.string(),
  voucherDetailsUrl: z.string().optional(),
  purchaseDate: z.string().datetime(),
  expirationDate: z.string().datetime(),
}) satisfies z.ZodType<OrderDetailsPurchaseVoucher>

// Schema per MerchantLoginInfo
const merchantLoginInfoSchema: z.ZodType<MerchantLoginInfo> = z.object({
  loginUrl: z.string(),
  loginDescription: z.string().optional(),
  personalData: z.array(z.string()).optional(),
  personalDataDisclaimer: z.string().optional(),
})

// Schema per OrderDetailsPurchaseGoodAndService
const orderDetailsPurchaseGoodAndServiceSchema = z.object({
  typology: z.literal('goodAndService'),
  purchaseDate: z.string().datetime(),
  purchaseDescription: z.string(),
  purchaseOrderId: z.string().optional(),
  voucherImageUrl: z.string(),
  voucherCode: z.string(),
  merchantLogin: merchantLoginInfoSchema.optional(),
}) satisfies z.ZodType<OrderDetailsPurchaseGoodAndService>

// Schema per VariantAndQuantity (necessario per MultiPurchase)
const variantAndQuantitySchema: z.ZodType<VariantAndQuantity> = z.object({
  variant: z.object({
    variantId: z.string(),
    externalId: z.string(),
    variantName: z.string(),
    priceDetails: z
      .object({
        facePrice: z.number(),
        finalGrossPrice: z.number(),
        taxPercentage: z.number(),
        taxClass: z.string().optional(),
        discountType: z.enum(['percentage', 'fixed']).optional(),
        discountValue: z.number().optional(),
        billingData: z.string().optional(),
      })
      .optional(),
    active: z.boolean(),
    signature: z.object({
      signature: z.string(),
      validUntil: z.number(),
    }),
    additionalInfo: z.record(z.string(), z.any()).optional(),
  }),
  quantity: z.number().int(),
})

// Schema per OrderDetailsPurchaseMultiPurchase
const orderDetailsPurchaseMultiPurchaseSchema = z.object({
  typology: z.literal('multiPurchase'),
  variants: z.array(variantAndQuantitySchema),
  childrenCount: z.number().int().optional(),
}) satisfies z.ZodType<OrderDetailsPurchaseMultiPurchase>

// Schema per OrderDetailsVoucherUsage
const orderDetailsVoucherUsageSchema = z.object({
  typology: z.literal('voucherUsage'),
  subject: z.string(),
  invoiceId: z.string(),
  customerId: z.string().optional(),
  fileURL: z.string(),
  filename: z.string(),
  raiFee: z.number().optional(),
  voucherIds: z.array(z.string()),
  contractId: z.string(),
  providerId: z.string(),
  invoiceDate: z.string(),
  invoiceTotal: z.number(),
  subCategoryId: z.string(),
  refundId: z.string(),
  message: z.string().optional(),
  refundKind: z.string().optional(),
  dueDate: z.string(),
  purchaseDate: z.string().datetime(),
}) satisfies z.ZodType<OrderDetailsVoucherUsage>

// Schema per OrderDetailsPurchaseDiscount
const orderDetailsPurchaseDiscountSchema = z.object({
  typology: z.literal('discount'),
  purchaseDate: z.string().datetime(),
  purchaseDescription: z.string().optional(),
  discountCode: z.string().optional(),
  discountDetailsUrl: z.string().optional(),
  discountActivationUrl: z.string().optional(),
  voucherImageUrl: z.string().optional(),
  voucherCode: z.string().optional(),
}) satisfies z.ZodType<OrderDetailsPurchaseDiscount>

// ✅ Schema discriminated union per OrderDetailsPurchase
const orderDetailsPurchaseSchema = z.discriminatedUnion('typology', [
  orderDetailsPurchaseGiftCardSchema,
  orderDetailsPurchasePassSchema,
  orderDetailsPurchaseVoucherSchema,
  orderDetailsPurchaseGoodAndServiceSchema,
  orderDetailsPurchaseMultiPurchaseSchema,
  orderDetailsVoucherUsageSchema,
  orderDetailsPurchaseDiscountSchema,
  orderDetailsPurchaseEmptySchema,
]) satisfies z.ZodType<OrderDetailsPurchase>

export const orderSchema: z.ZodType<OrderDetailsExtended> = z.object({
  orderId: z.string(),
  status: z.string(),
  orderDate: z.string(),
  detailPageUrl: z.string(),
  product: productSchema,
  variant: variantSchema,
  userInfo: userInfoSchema.optional(),
  productAdditionalInfo: z.record(z.string(), z.string()).optional(),
  beneficiaries: beneficiariesSchema.optional(),
  purchaseDetails: orderDetailsPurchaseSchema.optional(),
  priceDetails: OrderPriceDetailsSchema.optional(),
  isCancelable: z.boolean(),
})
